import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';

// Fetch planning data (planning items + tasks)
export function useGetPlanningData(filters = {}) {
  return useQuery({
    queryKey: ['planning', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.companyId) params.append('companyId', filters.companyId);
      if (filters.branchId) params.append('branchId', filters.branchId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.view) params.append('view', filters.view);

      const response = await api.get(`/planning?${params.toString()}`);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });
}

// Create planning item
export function useCreatePlanningItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/planning/items', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning'] });
    },
  });
}

// Update planning item
export function useUpdatePlanningItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/planning/items/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning'] });
    },
  });
}

// Delete planning item
export function useDeletePlanningItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/planning/items/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning'] });
    },
  });
}

// Update task schedule
export function useUpdateTaskSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/planning/tasks/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning'] });
    },
  });
}

// Assign resource (user) to task/planning item
// Supports both single userId (legacy) and array of userIds
export function useAssignResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemType, itemId, userId, userIds }) => {
      // Use userIds array if provided, otherwise wrap single userId
      const assignedUserIds = userIds || (userId ? [userId] : []);
      const assignedUserId = assignedUserIds[0] || null; // Keep legacy field in sync
      
      if (itemType === 'planning') {
        const response = await api.put(`/planning/items/${itemId}`, {
          assignedUserId,
          assignedUserIds
        });
        return response.data;
      } else if (itemType === 'task') {
        const response = await api.put(`/planning/tasks/${itemId}`, {
          assignedUserId,
          assignedUserIds
        });
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning'] });
    },
  });
}

// Fetch resources (users)
export function useGetResources() {
  return useQuery({
    queryKey: ['planning-resources'],
    queryFn: async () => {
      const response = await api.get('/planning/resources');
      return response.data;
    },
    staleTime: 60000, // 1 minute
  });
}

